import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatNumber } from "../lib/api";
import { ExportButtons } from "../lib/exports";
import {
  Loader2, Sparkles, BookOpen, AlertTriangle, HelpCircle,
  Package, Stethoscope,
} from "lucide-react";

function NomenclatorCard({ status }) {
  return (
    <div className="card mb-6 px-5 py-4 flex items-center gap-3" data-testid="nomenclator-card">
      <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center"><BookOpen size={17} /></div>
      <div className="flex-1">
        <div className="font-display font-bold">Nomenclátor e indicaciones terapéuticas</div>
        <div className="text-xs text-slate-500" data-testid="nomenclator-status">
          {status?.productos
            ? `Incluido en la aplicación · ${formatNumber(status.productos)} presentaciones${status.con_indicacion ? ` · ${formatNumber(status.con_indicacion)} con indicación terapéutica` : ""}`
            : "Preparando el nomenclátor integrado…"}
        </div>
      </div>
    </div>
  );
}

function TreatmentCard({ t }) {
  return (
    <div className="card p-6" data-testid={`treatment-${t.cn}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            <Stethoscope size={12} /> {t.atc || "Sin ATC"} · {t.principio_activo || "—"}
          </div>
          <h3 className="font-display text-lg font-bold mt-1">{t.nombre}</h3>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">{t.indicacion}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Dispensaciones</div>
          <div className="font-display text-2xl font-extrabold">{formatNumber(t.operaciones)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
        {(t.propuestas || []).map((p, i) => (
          <div key={i} className={"rounded-xl border p-4 " + (p.es_stock_parado ? "border-amber-400 bg-amber-50/50" : p.en_stock ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200")}
               data-testid={`proposal-${t.cn}-${i}`}>
            <div className="flex items-center gap-2 mb-1">
              {p.en_stock ? <Package size={13} className="text-emerald-600" /> : <Sparkles size={13} className="text-slate-400" />}
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                {p.es_stock_parado ? "Stock parado" : p.en_stock ? "En stock" : "Sin stock"}
              </span>
            </div>
            <div className="font-semibold text-sm leading-snug">{p.producto}</div>
            {p.cn && <div className="font-mono text-[11px] text-slate-500">CN {p.cn}</div>}
            <p className="text-xs text-slate-600 mt-2">{p.motivo}</p>
            {p.pregunta && (
              <p className="text-xs text-slate-700 mt-2 flex gap-1">
                <HelpCircle size={12} className="shrink-0 mt-0.5 text-slate-400" /><span>{p.pregunta}</span>
              </p>
            )}
            {p.alarma && (
              <p className="text-xs text-red-700 mt-2 flex gap-1">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" /><span>{p.alarma}</span>
              </p>
            )}
            {p.fuente && <div className="text-[10px] text-slate-400 mt-2">Fuente: {p.fuente}</div>}
          </div>
        ))}
      </div>

      {t.frase_mostrador && (
        <div className="mt-4 rounded-xl bg-slate-900 text-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Cómo ofrecerlo en el mostrador</div>
          <p className="text-sm mt-1">“{t.frase_mostrador}”</p>
        </div>
      )}
    </div>
  );
}

export default function Consejo() {
  const [status, setStatus] = useState(null);
  const [imports, setImports] = useState([]);
  const [importId, setImportId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, list] = await Promise.all([api.get("/nomenclator/status"), api.get("/sales/imports")]);
        setStatus(s.data);
        setImports(list.data);
        const withTeam = list.data.find((i) => i.kind === "farmatic_diario") || list.data[0];
        setImportId(withTeam?.id || null);
      } catch {
        setErr("No se pudo cargar la información");
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!importId) { setData(null); return; }
    let ignore = false;
    (async () => {
      try {
        const { data: d } = await api.get(`/cross-sell/${importId}`);
        if (!ignore) {
          setData(d);
          if (d.status === "running") setBusy(true);
        }
      } catch { /* sin propuestas todavía */ }
    })();
    return () => { ignore = true; };
  }, [importId]);

  const generate = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/cross-sell/suggest", { import_id: importId, top_n: 6 });
      // la IA tarda más que el límite de la pasarela → consultamos el progreso
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: d } = await api.get(`/cross-sell/${importId}`);
          if (d.status === "done") { setData(d); return; }
          if (d.status === "error") { setErr(d.error || "La IA no pudo generar las propuestas"); return; }
        } catch { /* corte puntual de red: seguimos consultando */ }
      }
      setErr("El análisis está tardando demasiado. Vuelve a intentarlo en unos minutos.");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "La IA no pudo generar las propuestas, inténtalo de nuevo");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 flex gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>;

  const flatRows = (data?.tratamientos || []).flatMap((t) =>
    (t.propuestas || []).map((p) => ({
      tratamiento: t.nombre, atc: t.atc, indicacion: t.indicacion, operaciones: t.operaciones,
      cn: p.cn || "", producto: p.producto, en_stock: p.en_stock ? "Sí" : "No",
      stock_parado: p.es_stock_parado ? "Sí" : "No", motivo: p.motivo,
      pregunta: p.pregunta, alarma: p.alarma, fuente: p.fuente,
    })));

  return (
    <div className="p-8 fade-in" data-testid="consejo-page">
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Consejo farmacéutico</div>
        <h1 className="font-display text-4xl font-extrabold mt-1 tracking-tight">Venta cruzada con IA</h1>
        <p className="text-slate-600 mt-1 text-sm max-w-3xl">
          Cruza las indicaciones terapéuticas del nomenclátor (ATC y principio activo) con el stock real de tu
          farmacia y propone qué aconsejar junto a cada tratamiento, con la pregunta previa y la señal de alarma.
        </p>
      </div>

      <NomenclatorCard status={status} />

      {!imports.length ? (
        <div className="card p-10 text-center text-slate-500" data-testid="consejo-no-sales">
          <Sparkles size={32} className="mx-auto text-slate-300" />
          <p className="mt-3 font-semibold">Sube antes las ventas del mes</p>
          <p className="text-sm">El consejo se calcula sobre los tratamientos que realmente dispensas.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {imports.map((im) => (
              <button key={im.id} onClick={() => setImportId(im.id)}
                      className={"px-3 py-2 rounded-lg text-sm font-semibold border transition " +
                        (importId === im.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400")}
                      data-testid={`consejo-import-${im.id}`}>
                {im.label}
              </button>
            ))}
            <button onClick={generate} disabled={busy || !status?.productos} className="btn btn-primary ml-auto" data-testid="generate-consejo">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Analizando tratamientos…</> : <><Sparkles size={14} /> Generar recomendaciones</>}
            </button>
            {flatRows.length > 0 && (
              <ExportButtons filename="consejo-venta-cruzada" title="Consejo y venta cruzada" sheetName="Consejo"
                             orientation="landscape"
                             columns={[{ header: "Tratamiento", key: "tratamiento" }, { header: "ATC", key: "atc" },
                                       { header: "Dispensaciones", key: "operaciones", align: "right" },
                                       { header: "CN", key: "cn" }, { header: "Producto a aconsejar", key: "producto" },
                                       { header: "En stock", key: "en_stock" }, { header: "Stock parado", key: "stock_parado" },
                                       { header: "Motivo", key: "motivo" }, { header: "Pregunta previa", key: "pregunta" },
                                       { header: "Señal de alarma", key: "alarma" }, { header: "Fuente", key: "fuente" }]}
                             rows={flatRows} testIdPrefix="export-consejo" />
            )}
          </div>

          {!status?.productos && (
            <div className="card mb-6 border-2 border-amber-400 bg-amber-50/60 px-5 py-3 text-sm text-amber-900" data-testid="need-nomenclator">
              El nomenclátor integrado se está preparando. Vuelve a intentarlo en un minuto.
            </div>
          )}
          {err && <div className="card mb-6 border border-red-300 bg-red-50 px-5 py-3 text-sm text-red-700" data-testid="consejo-error">{err}</div>}

          {busy && (
            <div className="card p-10 text-center text-slate-500" data-testid="consejo-loading">
              <Loader2 size={28} className="mx-auto animate-spin text-slate-400" />
              <p className="mt-3 text-sm">Analizando los tratamientos más dispensados y tu stock. Tarda unos segundos.</p>
            </div>
          )}

          {!busy && data?.tratamientos?.length > 0 && (
            <div className="space-y-6" data-testid="treatments-list">
              {data.tratamientos.map((t) => <TreatmentCard key={t.cn} t={t} />)}
              <p className="text-xs text-slate-500">
                Propuestas generadas con IA a partir de fuentes clínicas públicas. Revisión farmacéutica obligatoria
                antes de usarlas en el mostrador; no son recomendaciones oficiales de la AEMPS.
              </p>
            </div>
          )}

          {!busy && data && !data.tratamientos?.length && (
            <div className="card p-10 text-center text-slate-500" data-testid="consejo-empty">
              <Sparkles size={32} className="mx-auto text-slate-300" />
              <p className="mt-3 font-semibold">Todavía no hay recomendaciones para este mes</p>
              <p className="text-sm">Pulsa “Generar recomendaciones” para analizar los tratamientos más dispensados.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
